/**
 * WeekComparisonBadges
 * Exibe comparação de métricas entre semana atual e anterior
 *
 * Fase 1.2 do Plano de Evolução - Comparação Semana vs. Semana
 */

import { TrendingUp, TrendingDown, Minus, ArrowLeftRight } from 'lucide-react';
import { cn } from '@/utils/cn';
import { PainelCard } from './PainelCard';

export interface WeeklyComparison {
  deltas: {
    receivedDelta: number;
    processedDelta: number;
    discardedDelta: number;
    pendingDelta: number;
  };
  comparison: {
    weekStart: string;
    previousWeekStart: string;
    receivedChange: number;
    processedChange: number;
    discardedChange: number;
    pendingChange: number;
  };
}

interface WeekComparisonBadgesProps {
  comparison: WeeklyComparison;
  className?: string;
}

interface DeltaBadgeProps {
  label: string;
  delta: number;
  change: number;
  invertColor?: boolean; // Para métricas onde aumento é ruim (ex: pendentes)
}

function DeltaBadge({ label, delta, change, invertColor = false }: DeltaBadgeProps) {
  const isNeutral = delta === 0;
  const isPositive = delta > 0;
  // Melhora → primary; piora → destructive. Em métricas invertidas, aumento é piora.
  const isGood = invertColor ? !isPositive : isPositive;
  const Icon = isNeutral ? Minus : isPositive ? TrendingUp : TrendingDown;
  const sign = isPositive ? '+' : '';

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <div
        className={cn(
          'flex flex-wrap items-center gap-2 rounded-md px-2 py-1',
          isNeutral ? 'bg-muted' : isGood ? 'bg-primary/10' : 'bg-destructive/10'
        )}
      >
        <span
          className={cn(
            'flex items-center gap-1 text-sm font-semibold tabular-nums',
            isNeutral ? 'text-muted-foreground' : isGood ? 'text-primary' : 'text-destructive'
          )}
        >
          <Icon className="size-3" aria-hidden="true" />
          {sign}{delta}%
        </span>
        <span className="text-xs tabular-nums text-muted-foreground">
          ({sign}{change})
        </span>
      </div>
    </div>
  );
}

export function WeekComparisonBadges({ comparison, className = '' }: WeekComparisonBadgesProps) {
  const { deltas, comparison: comp } = comparison;

  return (
    <PainelCard
      title="Comparação com Semana Anterior"
      icon={ArrowLeftRight}
      className={className}
      action={
        <span>
          vs. {new Date(comp.previousWeekStart).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: 'short'
          })}
        </span>
      }
    >
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <DeltaBadge label="Recebidas" delta={deltas.receivedDelta} change={comp.receivedChange} />
        <DeltaBadge label="Tratadas" delta={deltas.processedDelta} change={comp.processedChange} />
        <DeltaBadge label="Descartadas" delta={deltas.discardedDelta} change={comp.discardedChange} />
        <DeltaBadge
          label="Não Tratadas"
          delta={deltas.pendingDelta}
          change={comp.pendingChange}
          invertColor={true} // Aumento em pendentes é negativo
        />
      </div>

      {deltas.receivedDelta !== 0 && (
        <p className="border-t border-border pt-4 text-xs text-muted-foreground">
          {deltas.receivedDelta > 0 ? (
            <>
              <span className="font-semibold text-foreground">↗ Volume aumentou</span>: {Math.abs(comp.receivedChange)} publicações a mais que a semana anterior
            </>
          ) : (
            <>
              <span className="font-semibold text-foreground">↘ Volume diminuiu</span>: {Math.abs(comp.receivedChange)} publicações a menos que a semana anterior
            </>
          )}
        </p>
      )}
    </PainelCard>
  );
}
